<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Seller Product</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify seller product assignment details and settings
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Seller Products
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Seller Product Assignment</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                </v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Assignment Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account-arrow-right</v-icon>
                    Assignment Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="userItems"
                        label="Seller *"
                        placeholder="Select a seller"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Seller is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.product_id"
                        :items="productItems"
                        label="Product *"
                        placeholder="Select a product"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Product is required']"
                        :error-messages="errors.product_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-package-variant</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Account Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Assignment Settings
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-check-circle</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">Assignment Status</div>
                              <div class="text-caption grey--text">Set the status for this assignment</div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              :disabled="!isEditMode"
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip 
                              small 
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? 'Active' : 'Inactive' }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ hasValidationErrors() ? 'Please fix validation errors' : 'Form validation passed' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="!isEditMode || isLoading"
                      v-show="isEditMode"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Assignment
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductEditPage",
  data() {
    return {
      form: {
        user_id: null,
        product_id: null,
        is_active: true,
      },
      valid: false,
      errors: {},
      isLoading: false,
      isEditMode: false, // Toggle between edit and read-only mode
      userItems: [],
      productItems: [],
      old_json: {},
    };
  },
  watch: {
    'form.user_id'() {
      this.clearError('user_id');
    },
    'form.product_id'() {
      this.clearError('product_id');
    },
    'form.is_active'() {
      this.clearError('is_active');
    },
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },
    
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async getMasters() {
      try {
        const response = await axios.get(`/masters?context=seller_product`);
        const masters = response.data;

        // Populate users (sellers)
        this.userItems = masters.users ? masters.users.map(user => ({
          value: user.id,
          text: `${user.first_name} ${user.last_name} (${user.email})`,
          id: user.id
        })) : [];

        // Populate products
        this.productItems = masters.products ? masters.products.map(product => ({
          value: product.id,
          text: `${product.name} (${product.sku_id})`,
          id: product.id
        })) : [];

      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.sellerProductId;
      try {
        const response = await axios.get(`/seller_products/${encryptedId}`);
        this.form = response.data.data;
        this.old_json = response.data.data;
        console.log(this.form);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
      this.isLoading = false;
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.isLoading = true;
        this.errors = {};

        await axios.patch(`/seller_products/${this.$route.params.sellerProductId}`, this.form);
        this.isLoading = false;
        this.$router.push("/seller-products");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating seller product:", error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .form-title {
    font-size: 0.9rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Read-Only Mode Styling */
.professional-field >>> .v-input--is-disabled {
  opacity: 0.7;
}

.professional-field >>> .v-input--is-disabled .v-input__slot {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}

.professional-field >>> .v-input--is-disabled .v-label {
  color: #757575 !important;
}

.professional-field >>> .v-input--is-disabled input {
  color: #616161 !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>